/**
 * useVisitTracking — fires a fire-and-forget POST to
 * /api/analytics/visit every time the SPA route changes.
 *
 * Why a frontend ping rather than server middleware:
 *   - The backend can't tell a real navigation from a CSS/JS bundle
 *     fetch or a static asset request. A frontend hook fires exactly
 *     once per pushState navigation — which is what "a visit" means
 *     to a non-engineer.
 *   - No CORS gymnastics: same origin, no preflight (we POST plain
 *     JSON which is a simple request).
 *
 * What we deliberately do NOT do:
 *   - No tracking on /admin/* (we don't want our own admin clicks in
 *     the public-traffic chart).
 *   - No tracking on the customer portal (private, separately auth'd).
 *   - No query strings, fragments, or per-user identifiers — the
 *     backend re-sanitises, but we strip here too so the network tab
 *     stays clean for anyone curious.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Routes we deliberately exclude from the traffic count.
const EXCLUDED_PREFIXES = ["/admin", "/customer-portal"];

function isExcluded(pathname) {
    return EXCLUDED_PREFIXES.some((p) => pathname.startsWith(p));
}

export default function useVisitTracking() {
    const location = useLocation();
    useEffect(() => {
        const path = location.pathname || "/";
        if (isExcluded(path)) return;
        // Fire-and-forget. Any network failure is silent — analytics
        // must never block or alert the user.
        axios
            .post(
                `${API}/analytics/visit`,
                { path },
                { withCredentials: false }
            )
            .catch(() => {});
    }, [location.pathname]);
}
